import Algolia, { SearchResponse } from '@/scripts/libs/algolia'
import { AlgoliaProductHit } from '@/scripts/libs/algolia/types/plp'
import AlgoliaProductsUtils from '@/scripts/libs/algolia/products'
import AlgoliaRecommend from '@/scripts/libs/algolia/recommend'
import axios from 'axios'
import wcs from '@/scripts/libs/wcs'
class AlgoliaEditorial {
  async getEditorialProducts({
    isCluster,
    groupedRule,
    maxNumber,
    ungroupedRule,
  }: {
    isCluster: boolean
    groupedRule: string
    maxNumber: number
    ungroupedRule: string
  }) {
    if (!isCluster) {
      const productsAndFiltersResults = await Algolia.ungroupedSearch({
        queries: [
          {
            ruleContexts: [ungroupedRule],
            hitsPerPage: maxNumber,
            attributesToRetrieve: ['*'],
            facets: ['attributes_translated.*', 'sort*'],
            clickAnalytics: true,
          },
        ],
      })

      const productsResult = productsAndFiltersResults
        .results[0] as SearchResponse<AlgoliaProductHit>
      const hits = productsResult.hits

      let products = []

      products = hits.map((hit, index) => {
        const carouselOrder = hit?.variants?.length
          ? hit.variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
          : [{ productPartNumber: hit.parentPartnumberId }]

        return AlgoliaProductsUtils.normalizeProduct({
          product: hit,
          algoliaTracking: {
            grouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getGroupedIndexNameOrDefault(),
              objectIDs: [hit.objectID],
              position: index,
            },
            ungrouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getUngroupedIndexNameOrDefault(),
              objectIDs: [hit.objectID],
              position: index,
            },
            carouselOrder,
          },
        })
      })
      return products
    }

    const algoliaProducts = await Algolia.multipleGroupedSearch([
      {
        params: {
          ruleContexts: [groupedRule],
          hitsPerPage: maxNumber,
          attributesToRetrieve: ['*'],
          facets: ['attributes_translated.*', 'sort*'],
        },
      },
    ])

    const productsResult = algoliaProducts.results[0] as SearchResponse<AlgoliaProductHit>

    const hits = productsResult.hits
    let products = []

    if (hits && hits.length) {
      const queries = hits.map((hit) => ({
        hitsPerPage: maxNumber,
        ruleContexts: [ungroupedRule],
        facetFilters: [`x_groupkey: ${hit.x_groupkey}`],
        clickAnalytics: true,
      }))

      const ungroupedSearchResult = await Algolia.ungroupedSearch<AlgoliaProductHit>({
        queries,
      })

      products = hits.map((hit, index) => {
        const variants = ungroupedSearchResult.results.find((ungroupedProduct) =>
          ungroupedProduct.hits.some((ungroupedHit) => ungroupedHit.x_groupkey === hit.x_groupkey)
        )?.hits

        const carouselOrder = variants?.length
          ? variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
          : [{ productPartNumber: hit.parentPartnumberId }]
        return AlgoliaProductsUtils.normalizeProduct({
          product: {
            ...hit,
            variants,
          },
          algoliaTracking: {
            grouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getGroupedIndexNameOrDefault(),
              objectIDs: variants?.map((hit) => hit.partnumberId),
              position: index,
            },
            ungrouped: {
              queryID: productsResult.queryID,
              indexName: Algolia.getUngroupedIndexNameOrDefault(),
              objectIDs: variants?.map((hit) => hit.partnumberId),
              position: index,
            },
            carouselOrder,
          },
        })
      })
      return products
    }
  }

  async getTrendingItems({
    facetName,
    facetValue,
    isCluster,
    maxNumber,
    threshold,
  }: {
    facetName?: string
    facetValue?: string
    isCluster: boolean
    maxNumber: number
    threshold?: number
  }) {
    const response = await AlgoliaRecommend.getTrendingItems({
      facetName,
      facetValue,
      hitsPerPage: maxNumber,
      threshold,
    })
    const trendingItems = response.results[0] as SearchResponse<AlgoliaProductHit>
    const hits = trendingItems.hits
    const normalizedItems = hits.map((hit, index) => {
      const carouselOrder = hit?.variants?.length
        ? hit.variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
        : [{ productPartNumber: hit.parentPartnumberId }]

      return AlgoliaProductsUtils.normalizeProduct({
        product: hit,
        algoliaTracking: {
          grouped: {
            queryID: trendingItems.queryID,
            indexName: Algolia.getGroupedIndexNameOrDefault(),
            objectIDs: [hit.objectID],
            position: index,
          },
          ungrouped: {
            queryID: trendingItems.queryID,
            indexName: Algolia.getUngroupedIndexNameOrDefault(),
            objectIDs: [hit.objectID],
            position: index,
          },
          carouselOrder,
        },
      })
    })

    if (!isCluster) {
      return normalizedItems.slice(0, maxNumber)
    }

    const queries = hits.map((hit) => ({
      facetFilters: [`x_groupkey: ${hit.x_groupkey}`],
      clickAnalytics: true,
    }))

    const ungroupedSearchResult = await Algolia.ungroupedSearch<AlgoliaProductHit>({
      queries,
    })

    const products = hits.map((hit, index) => {
      const variants = ungroupedSearchResult.results.find((ungroupedProduct) =>
        ungroupedProduct.hits.some((ungroupedHit) => ungroupedHit.x_groupkey === hit.x_groupkey)
      )?.hits

      const carouselOrder = variants?.length
        ? variants?.map((variant) => ({ productPartNumber: variant.parentPartnumberId }))
        : [{ productPartNumber: hit.parentPartnumberId }]
      return AlgoliaProductsUtils.normalizeProduct({
        product: {
          ...hit,
          variants,
        },
        algoliaTracking: {
          grouped: {
            queryID: trendingItems.queryID,
            indexName: Algolia.getGroupedIndexNameOrDefault(),
            objectIDs: variants?.map((hit) => hit.partnumberId),
            position: index,
          },
          ungrouped: {
            queryID: trendingItems.queryID,
            indexName: Algolia.getUngroupedIndexNameOrDefault(),
            objectIDs: variants?.map((hit) => hit.partnumberId),
            position: index,
          },
          carouselOrder,
        },
      })
    })
    return products.slice(0, maxNumber)
  }

  async getTrendingFacets({
    facetName,
    maxNumber,
    threshold = 80,
  }: {
    facetName: string
    maxNumber: number
    threshold?: number
  }) {
    const algoliaTrendingFacets = await AlgoliaRecommend.getTrendingFacets(facetName, threshold)
    const categoryNames = algoliaTrendingFacets.results[0].hits.map((hit) => hit.facetValue)
    const url = wcs.getEndPoint('trendingFacets')
    const response = await axios.post(url, {
      categoryNames,
    })
    const trendingFacets = response?.data.categorySEODetails?.map(
      (seoDetail: Record<string, { title: string; url: string }>) => {
        const key = Object.keys(seoDetail)[0]
        return {
          category: key,
          href: seoDetail[key].url,
          label: seoDetail[key].title,
        }
      }
    )
    return trendingFacets.slice(0, maxNumber) ?? []
  }
}

export default new AlgoliaEditorial()
